import { BaseService } from "./base.service";
import {
	IBidStepHistory,
	IBidSummaryGridDetail,
	IPreferredBidSupplier,
	ISearchSupplierModel,
	PreferredBidSupplierModel,
} from "models/Bid/BidSummaryGridDetail";
import { TableFilterModel } from "models/TableFilterModel";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import { IBidNumber } from "models/Bid/BidNumberModel";
import { AddBidModel } from "models/Bid/AddBidModel";
import { IBidStatus } from "models/Bid/BidStatusModel";
import {
	BidDetailModel,
	IBidConversionType,
	ReadyStatusBidDetail,
} from "models/Bid/BidDetailModel";
import { BidSystemTemplateSectionDetail } from "models/Bid/BidSystemTemplateSectionDetail";
import { ConversionToBidDocumentDetail } from "models/Bid/ConversionToBidDocumentDetail";
import {
	BidSbdDocumentDetailModel,
	BidSbdDocumentModel,
	IBidDetailForSbdDocument,
	IBidSbdDocument,
} from "models/Bid/BidSbdDocumentModel";
import { IItem } from "models/ItemModel";
import { IProspectiveSupplierDetailModel } from "models/Bid/ProspectiveSupplierDetail";
import { IBidCommitteeTabGridDetail } from "models/Bid/BidCommitteeTabSummaryGridModel";
import { IBidCommitteeAssignment } from "models/BidCommittee/BidCommittee";
import {
	BidEvaluationPhaseSummaryGridModel,
	IBidEvaluationPhase,
	IBidEvaluationPhaseTileModel,
} from "models/Bid/BidEvaluationPhaseModel";
import {
	ISubmittedBidSupplierDetail,
	SubmittedBidGridResponseModel,
} from "models/Bid/SubmittedBidGridListModel";
import {
	ICompletedBidStepHistory,
	IPreviousBidStepHistory,
} from "models/Bid/BidStepModel";
import { IDelegateToUser } from "models/Requisition/DelegateToUserSelection";
import { BidQueryUserModel } from "models/Bid/BidQueryUserModel";
import { SupplierData } from "models/SupplierUserModel";
import { IRequisitionNumber } from "models/RequisitionNumberModel";

class BidServiceInternal extends BaseService {
	getBidList = async (
		model: TableFilterModel
	): Promise<IBidSummaryGridDetail[]> =>
		this.callApi<IBidSummaryGridDetail[]>(
			`Bid/GetBidList`,
			"POST",
			undefined,
			model
		);

	getBidNumberList = async (
		model: ITableNumberColumnFilter
	): Promise<IBidNumber[]> =>
		this.callApi<IBidNumber[]>(
			`Bid/GetBidNumberList`,
			"POST",
			undefined,
			model
		);

	getBidStatusList = async (): Promise<IBidStatus[]> =>
		this.callApi<IBidStatus[]>(`BidStatus/GetBidStatusList`, "GET");

	saveBidDetail = async (
		model: AddBidModel
	): Promise<{ bidId: number; bidNumber: string }> =>
		this.callApi<{ bidId: number; bidNumber: string }>(
			`Bid/Save`,
			"POST",
			undefined,
			model
		);

	getBidDetailById = async (
		bidId: number,
		bidStepId: number
	): Promise<BidDetailModel> =>
		this.callApi<BidDetailModel>(
			`Bid/GetBidDetail/${bidId}`,
			"GET",
			bidStepId > 0
				? new URLSearchParams({
						bidStepId: bidStepId.toString(),
				  })
				: undefined
		);

	updateBidDetail = async (
		bidId: number,
		model: AddBidModel
	): Promise<number> =>
		this.callApi<number>(`Bid/Update/${bidId}`, "POST", undefined, model);

	getBidDocumentDataForConversionToBid = async (
		requisitionIds: string
	): Promise<ConversionToBidDocumentDetail[]> =>
		this.callApi<ConversionToBidDocumentDetail[]>(
			`SystemTemplateSection/GetBidDocumentDataForConversionToBid/${requisitionIds}`,
			"GET"
		);

	getBidDocuments = async (
		bidId: number
	): Promise<BidSystemTemplateSectionDetail[]> =>
		this.callApi<BidSystemTemplateSectionDetail[]>(
			`BidSystemTemplateSection/GetBidDocuments/${bidId}`,
			"GET"
		);

	saveUpdateAllBidTemplateSection = async (
		bidId: number,
		bidSectionDetailList: BidSystemTemplateSectionDetail[]
	): Promise<BidSystemTemplateSectionDetail[]> =>
		this.callApi<BidSystemTemplateSectionDetail[]>(
			`BidSystemTemplateSection/SaveUpdateAll/${bidId}`,
			"POST",
			undefined,
			bidSectionDetailList
		);

	bidSystemTemplateSectionPreview = async (bidId: number): Promise<string> =>
		this.callApi<string>(`BidSystemTemplateSection/Preview/${bidId}`, "GET");

	bidSbdDocumentPreview = async (
		bidId: number,
		sbdDocumentId: number
	): Promise<string> =>
		this.callApi<string>(
			`BidSbdDocument/Preview/${bidId}/${sbdDocumentId}`,
			"GET"
		);

	saveUpdateBidSbdDocument = async (
		model: BidSbdDocumentDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidSbdDocument/SaveUpdate`,
			"POST",
			undefined,
			model
		);

	saveUpdateAllBidSbdDocument = async (
		bidId: number,
		model: BidSbdDocumentModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidSbdDocument/SaveUpdateAll/${bidId}`,
			"POST",
			undefined,
			model
		);

	getBidSbdDocumentById = async (
		bidId: number,
		sbdDocumentId: number
	): Promise<IBidSbdDocument> =>
		this.callApi<IBidSbdDocument>(
			`BidSbdDocument/GetById/${bidId}/${sbdDocumentId}`,
			"GET"
		);

	getBidDetailForSBDDocument = async (
		bidId: number
	): Promise<IBidDetailForSbdDocument> =>
		this.callApi<IBidDetailForSbdDocument>(
			`Bid/GetBidDetailForSBDDocument/${bidId}`,
			"GET"
		);

	getBidConversionTypeList = async (): Promise<IBidConversionType[]> =>
		this.callApi<IBidConversionType[]>(`BidConversionType/GetAll`, "GET");

	getItemByBidId = async (bidId: number): Promise<IItem[]> =>
		this.callApi<IItem[]>(`Bid/GetItemByBidId/${bidId}`, "GET");

	getBidStepList = async (bidId: number): Promise<IBidStepHistory[]> =>
		this.callApi<IBidStepHistory[]>(`BidStep/GetBidStepList/${bidId}`, "GET");

	submitBid = async (bidId: number, comment: string): Promise<number> =>
		this.callApi<number>(`Bid/SubmitBid/${bidId}`, "POST", undefined, {
			comment: comment,
		});

	getReadyStatusBidDetails = async (
		bidId: string,
		bidStepId: string
	): Promise<ReadyStatusBidDetail> =>
		this.callApi<ReadyStatusBidDetail>(
			`Bid/GetReadyStatusBidDetails`,
			"POST",
			undefined,
			{
				Key: bidId.toString(),
				Key_1: bidStepId.toString(),
			}
		);

	getDecryptedBidId = async (key: string): Promise<number> =>
		this.callApi<number>(`Bid/GetDecryptedBidId`, "POST", undefined, {
			Key: key,
		});

	generateBid = async (bidId: number): Promise<boolean> =>
		this.callApi<boolean>(`Bid/Generate/${bidId}`, "POST", undefined);

	searchSupplier = async (
		model: TableFilterModel
	): Promise<ISearchSupplierModel[]> =>
		this.callApi<ISearchSupplierModel[]>(
			`Bid/SearchSupplier`,
			"POST",
			undefined,
			model
		);

	getPreferredBidSupplier = (bidId: number): Promise<IPreferredBidSupplier> =>
		this.callApi<IPreferredBidSupplier>(
			`Bid/GetPreferredBidSupplier/${bidId}`,
			"GET"
		);

	saveUpdatePreferredBidSupplier = async (
		bidId: number,
		model: PreferredBidSupplierModel
	): Promise<{ bidProspectiveSupplierHistoryId: number }> =>
		this.callApi<{ bidProspectiveSupplierHistoryId: number }>(
			`Bid/SaveUpdatePreferredBidSupplier/${bidId}`,
			"POST",
			undefined,
			model
		);

	getBidTotal = async (bidId: number): Promise<number> =>
		this.callApi<number>(`Bid/GetBidTotal/${bidId}`, "GET");

	saveUpdateProspectiveSupplierDetails = async (
		bidId: number,
		model: IPreferredBidSupplier
	): Promise<number> =>
		this.callApi<number>(
			`Bid/SaveUpdateProspectiveSupplierDetails`,
			"POST",
			bidId > 0
				? new URLSearchParams({
						bidId: bidId.toString(),
				  })
				: undefined,
			model
		);

	getBidLineItemCommodityCodeList = async (bidId: number): Promise<string[]> =>
		this.callApi<string[]>(
			`BidLine/GetBidLineItemCommodityCodeList`,
			"GET",
			bidId > 0
				? new URLSearchParams({
						bidId: bidId.toString(),
				  })
				: undefined,
			undefined
		);

	getBidProspectiveSupplierDetails = async (
		bidId: number,
		bidStepId: number
	): Promise<IProspectiveSupplierDetailModel> =>
		this.callApi<IProspectiveSupplierDetailModel>(
			`Bid/GetBidProspectiveSupplierDetails/${bidId}`,
			"GET",
			bidStepId && bidStepId > 0
				? new URLSearchParams({
						bidStepId: bidStepId.toString(),
				  })
				: undefined
		);

	saveUpdateBidCommitteeLineDetail = async (
		bidId: number,
		request: number[]
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BidCommitteeLine/SaveUpdate`,
			"POST",
			bidId > 0
				? new URLSearchParams({
						bidId: bidId.toString(),
				  })
				: undefined,
			request
		);

	getBidCommitteeLineDetails = async (
		bidId: number
	): Promise<IBidCommitteeTabGridDetail[]> =>
		this.callApi<IBidCommitteeTabGridDetail[]>(
			`BidCommitteeLine/GetBidCommitteeLineDetails/${bidId}`,
			"GET"
		);

	getBidCommitteeAssignmentList = async (
		bidId: number
	): Promise<IBidCommitteeAssignment[]> =>
		this.callApi<IBidCommitteeAssignment[]>(
			`Bid/GetBidCommitteeAssignment/${bidId}`,
			"GET"
		);

	deleteBidCommitteeLineDetail = async (
		bidCommitteeLineId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BidCommitteeLine/Delete/${bidCommitteeLineId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	saveBidEvaluationPhase = async (
		bidId: number,
		evaluationPhaseId: number
	): Promise<number> =>
		this.callApi<number>(
			`BidEvaluationPhase/Save/${bidId}/${evaluationPhaseId}`,
			"POST",
			undefined,
			undefined
		);

	deleteBidEvaluationPhase = async (
		bidId: number,
		evaluationPhaseId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`BidEvaluationPhase/Delete/${bidId}/${evaluationPhaseId}`,
			"DELETE",
			undefined,
			undefined
		);

	getBidEvaluationPhaseByBidId = async (
		bidId: number
	): Promise<IBidEvaluationPhase[]> =>
		this.callApi<IBidEvaluationPhase[]>(
			`BidEvaluationPhase/GetAll/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	getSubmittedBidList = async (
		bidId: number,
		isCfe: boolean
	): Promise<SubmittedBidGridResponseModel> =>
		this.callApi<SubmittedBidGridResponseModel>(
			`Bid/GetSubmittedBidList/${bidId}/${isCfe}`,
			"GET",
			undefined,
			undefined
		);

	getSubmittedBidSupplierList = async (
		bidId: number
	): Promise<ISubmittedBidSupplierDetail[]> =>
		this.callApi<ISubmittedBidSupplierDetail[]>(
			`Bid/GetSubmittedVendorBidSuppliers/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	getVendorPortalBidSupplierIds = async (bidId: number): Promise<number[]> =>
		this.callApi<number[]>(
			`Bid/VendorPortalBidSupplierIds/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	getManualBidUploadSupplierListByBidId = async (
		bidId: number
	): Promise<SupplierData[]> =>
		this.callApi<SupplierData[]>(
			`Bid/GetManualBidUploadSupplierList/${bidId}`,
			"GET"
		);

	updateBidEvaluationPhaseStatus = async (bidId: number): Promise<boolean> =>
		this.callApi<boolean>(
			`BidEvaluationPhase/UpdateBidEvaluationPhaseStatus/${bidId}`,
			"POST",
			undefined,
			undefined
		);

	updateBidStepCompleteAndApprove = async (
		bidId: number,
		bidStepId: number,
		comment: string
	): Promise<{
		isThisLastStep: boolean;
		isReadyStatusAllTaskCompleted: boolean;
	}> =>
		this.callApi<{
			isThisLastStep: boolean;
			isReadyStatusAllTaskCompleted: boolean;
		}>(
			`BidStep/UpdateBidStepCompleteAndApprove/${bidId}/${bidStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	getStepsBeforeCurrentStep = async (
		bidId: number,
		currentStepBidStepId: number
	): Promise<IPreviousBidStepHistory[]> =>
		this.callApi<IPreviousBidStepHistory[]>(
			`BidStep/GetStepsBeforeCurrentStep/${bidId}/${currentStepBidStepId}`,
			"GET"
		);

	updateBidStepStatusChangeRequest = async (
		bidId: number,
		selectedStepBidStepHistoryId: number,
		currentStepBidStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidStep/UpdateBidStepStatusChangeRequest/${bidId}`,
			"POST",
			undefined,
			{
				comment: comment,
				selectedStepBidStepHistoryId: selectedStepBidStepHistoryId.toString(),
				currentStepBidStepHistoryId: currentStepBidStepHistoryId.toString(),
			}
		);

	updateBidStepStatusRecall = async (
		bidId: number,
		completedStepBidStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidStep/UpdateBidStepStatusRecall/${bidId}`,
			"POST",
			undefined,
			{
				completedStepBidStepHistoryId: completedStepBidStepHistoryId.toString(),
				comment: comment,
			}
		);

	reSubmitBid = async (
		bidId: number,
		currentStepBidStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidStep/ReSubmitBid/${bidId}/${currentStepBidStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	rejectBid = async (
		bidId: number,
		bidStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidStep/RejectBid/${bidId}/${bidStepHistoryId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	getDelegatedUserList = async (
		bidStepHistoryId: number
	): Promise<IDelegateToUser[]> =>
		this.callApi<IDelegateToUser[]>(
			`BidStep/GetDelegatedUserList/${bidStepHistoryId}`,
			"GET"
		);

	delegateBid = async (
		bidId: number,
		bidStepId: number,
		userId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`BidStep/DelegateBid/${bidId}/${bidStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
				userId: userId.toString(),
			}
		);

	getCompletedBidStepDetail = async (
		bidId: number
	): Promise<ICompletedBidStepHistory> =>
		this.callApi<ICompletedBidStepHistory>(
			`BidStep/GetCompletedBidStepDetail/${bidId}`,
			"GET"
		);

	getBidEvaluationPhaseSummary = async (
		bidId: number
	): Promise<BidEvaluationPhaseSummaryGridModel> =>
		this.callApi<BidEvaluationPhaseSummaryGridModel>(
			`BidEvaluationPhase/GetBidEvaluationPhaseSummary/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	getBidEvaluationPhaseTile = async (
		bidId: number
	): Promise<IBidEvaluationPhaseTileModel[]> =>
		this.callApi<IBidEvaluationPhaseTileModel[]>(
			`BidEvaluationPhase/GetBidEvaluationPhaseTile/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	getBidQueryUsers = async (bidId: number): Promise<BidQueryUserModel> =>
		this.callApi<BidQueryUserModel>(
			`Bid/GetBidQueryUsers/${bidId}`,
			"GET",
			undefined,
			undefined
		);

	saveUpdateBidQueryUser = async (model: BidQueryUserModel): Promise<boolean> =>
		this.callApi<boolean>(
			`Bid/SaveUpdateBidQueryUser`,
			"POST",
			undefined,
			model
		);

	getBidRequisitionNumbers = async (
		reportId?: number | null
	): Promise<IRequisitionNumber[]> =>
		this.callApi<IRequisitionNumber[]>(
			`Bid/GetBidRequisitionNumbers${reportId ? `?reportId=${reportId}` : ""}`,
			"GET"
		);
	generateBidReport = async (
		model: TableFilterModel,
		reportId: number,
		reportTypeId: number,
		isFromDetail: boolean = false
	): Promise<string> =>
		this.callApi<string>(
			`Bid/GenerateBidReport/${reportId}/${reportTypeId}?IsFromDetail=${isFromDetail}`,
			"POST",
			undefined,
			model
		);
	getReportBidNumberList = async (
		model: ITableNumberColumnFilter,
		reportId?: number | null
	): Promise<IBidNumber[]> =>
		this.callApi<IBidNumber[]>(
			`Bid/GetReportBidNumberList${reportId ? `?reportId=${reportId}` : ""}`,
			"POST",
			undefined,
			model
		);
}

export const BidService = new BidServiceInternal();
